import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useParams } from 'react-router-dom';
import { AiOutlineHeart, AiFillHeart, AiOutlineShoppingCart } from 'react-icons/ai';
import { BsStarFill, BsStarHalf, BsStar } from 'react-icons/bs';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../Header/Header';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { addToCart } from '../../redux/slices/cartSlice';
import { globalLogout } from '../../Utils/Logout';
import Footer from '../../Components/Footer/Footer';
import { 
  fetchWishlist,
  addItemToWishlist,
  removeItemFromWishlist
} from '../../redux/thunks/wishlistThunks';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  },
  hover: {
    y: -5,
    scale: 1.02,
    transition: {
      duration: 0.2
    }
  }
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)",
    transition: {
      duration: 0.2
    }
  },
  tap: {
    scale: 0.95
  }
};

const ProductsByCategory = () => {
  const { categoryId } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState(null);
  const [productRatings, setProductRatings] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const token = Cookies.get("ecom_user_token");
  const { items: wishlist, loading: wishlistLoading } = useSelector(state => state.wishlist);

  // Fetch initial wishlist when component mounts
  useEffect(() => {
    if (token) {
      dispatch(fetchWishlist());
    }
  }, [token, dispatch]);

  // Function to fetch ratings for a product
  const fetchProductRatings = async (productId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/rating/productRating/${productId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      const ratings = response.data.ratings || [];
      return ratings;
    } catch (error) {
        globalLogout(error)      
    }
  };

  // Calculate average rating for a product
  const calculateAverageRating = (ratings) => {
    if (ratings.length === 0) return 0;
    const sum = ratings.reduce((acc, review) => acc + review.ratings, 0);
    return sum / ratings.length;
  };

  const handleAddToCart = async (product, e) => {
    e.stopPropagation();
    if (product.stock <= 0) {
      alert("This product is out of stock");
      return;
    }
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/add-to-cart`,
        {
          productId: product._id,
          quantity: 1
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      navigate('/cart');
    } catch (error) {
      // console.error("Add to cart API failed:", error);
      // alert("Failed to add to cart. Please try again.");
      globalLogout(error)
    }
  };

  const toggleWishlist = async (productId, e) => {
    e.stopPropagation();
    if (!token) {
      alert("Please login to manage your wishlist");
      navigate('/login');
      return;
    }

    try {
      if (wishlist.some(item => item.productId === productId)) {
        await dispatch(removeItemFromWishlist(productId));
      } else {
        await dispatch(addItemToWishlist(productId));
      }
    } catch (error) {
      console.error("Wishlist operation failed:", error);
      alert("Failed to update wishlist. Please try again.");
    }
  };

  const fetchProductDetails = (slug) => {
    navigate(`/product/${slug}`);
  };

  useEffect(() => {
    const fetchProducts = async () => {
      if (!categoryId) return;

      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/get-by-category/${categoryId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        // Filter out inactive products
        const activeProducts = response.data.products.filter(
          product => product.status === 'approved'
        );
        setProducts(activeProducts || []);
        setCategory(response?.data?.products[0]?.category?.name);

        // Fetch ratings for each product
        const ratingsMap = {};
        for (const product of activeProducts) {
          const ratings = await fetchProductRatings(product._id);
          ratingsMap[product._id] = ratings;
        }
        setProductRatings(ratingsMap);
      } catch (err) {
        setError(err.message || "Something went wrong.");
        globalLogout(err)
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categoryId, token]);

  const renderRatingStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<BsStarFill key={`full-${i}`} className="text-yellow-400" />);
    }
    
    if (hasHalfStar) {
      stars.push(<BsStarHalf key="half" className="text-yellow-400" />);
    }
    
    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<BsStar key={`empty-${i}`} className="text-yellow-400" />);
    }
    
    return stars;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full"
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white p-8 rounded-xl shadow-lg max-w-md text-center"
          >
            <h2 className="text-2xl font-bold text-red-500 mb-4">Error Loading Products</h2>
            <p className="text-gray-600">{error}</p>
            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={() => window.location.reload()}
              className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg font-medium"
            >
              Try Again
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Category Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12 text-center"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            {category || 'Our Products'}
          </h1>
          <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
        </motion.div>

        {/* Products Grid */}
        {products.length > 0 ? (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
          >
            {products.map((product) => {
              const ratings = productRatings[product._id] || [];
              const avgRating = calculateAverageRating(ratings);
              const reviewCount = ratings.length;
              const isInWishlist = wishlist.some(item => item.productId === product._id);

              return (
                <motion.div
                  key={product._id}
                  variants={itemVariants}
                  whileHover="hover"
                  onClick={() => fetchProductDetails(product.slug)}
                  className={`bg-white rounded-xl shadow-md overflow-hidden cursor-pointer relative transition-all duration-300 ${
                    product.stock <= 0 ? 'opacity-70 grayscale' : ''
                  }`}
                >
                  {/* Product Image */}
                  <div className="relative h-64 overflow-hidden group">
                    <img
                      src={product.images[0]?.blob || 'https://via.placeholder.com/300'}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    
                    {/* Wishlist Button */}
                    <motion.button
                      onClick={(e) => toggleWishlist(product._id, e)}
                      disabled={wishlistLoading || product.stock <= 0}
                      className={`absolute top-3 right-3 p-2 rounded-full bg-white bg-opacity-80 backdrop-blur-sm shadow-md ${
                        product.stock <= 0 ? 'cursor-not-allowed' : ''
                      }`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      {wishlistLoading ? (
                        <div className="h-5 w-5 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                      ) : isInWishlist ? (
                        <AiFillHeart className="text-red-500 h-5 w-5" />
                      ) : (
                        <AiOutlineHeart className="text-gray-600 h-5 w-5" />
                      )}
                    </motion.button>
                    
                    {/* Stock Status */}
                    {product.stock <= 0 && (
                      <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                        <span className="bg-white px-4 py-1 rounded-full text-sm font-bold text-gray-800">
                          Out of Stock
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Product Info */}
                  <div className="p-5">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-lg font-bold text-gray-800 line-clamp-1">
                        {product?.name}
                      </h3>
                      <span className="text-lg font-bold text-blue-600 whitespace-nowrap ml-2">
                        ₹{product?.price?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {product?.description}
                    </p>
                    
                    {/* Rating */}
                    <div className="flex items-center mb-4">
                      <div className="flex mr-2">
                        {renderRatingStars(avgRating)}
                      </div>
                      <span className="text-xs text-gray-500">
                        ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
                      </span>
                    </div>
                    
                    {/* Add to Cart Button */}
                    <motion.button
                      onClick={(e) => handleAddToCart(product, e)}
                      disabled={product?.stock <= 0}
                      variants={buttonVariants}
                      whileHover={product.stock > 0 ? "hover" : {}}
                      whileTap={product.stock > 0 ? "tap" : {}}
                      className={`w-full py-3 rounded-lg font-medium flex items-center justify-center space-x-2 ${
                        product.stock > 0 
                          ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white' 
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <AiOutlineShoppingCart className="text-lg" />
                      <span>{product.stock > 0 ? 'Add to Cart' : 'Out of Stock'}</span>
                    </motion.button>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-16"
          >
            <div className="bg-gray-200 p-8 rounded-full mb-6">
              <AiOutlineShoppingCart className="text-gray-400 text-5xl" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No Products Found
            </h3>
            <p className="text-gray-500 mb-6 text-center max-w-md">
              There are currently no active products in this category. Please check back later.
            </p>
            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium"
            >
              Continue Shopping
            </motion.button>
          </motion.div>
        )}
      </main>
      
   <Footer />
    </div>
  );
};

export default ProductsByCategory;
